import api from './api';
import {
  QuestionnaireType,
  PatientQuestionnaire,
  PatientQuestionnaireWithAnswers,
  BulkAnswersRequest,
  SectionAnswerData,
} from '../types/questionnaire.types';
import { MedicationMonitoringFlag } from './patientService';

export const questionnaireService = {
  // Questionnaire Types - Admin only
  async getQuestionnaireTypes(tenantId: number): Promise<QuestionnaireType[]> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
    });
    const response = await api.get(`/questionnaire-types/?${params}`);
    return response.data;
  },

  async createQuestionnaireType(
    tenantId: number,
    data: { name: string; title: string; path: string }
  ): Promise<QuestionnaireType> {
    const response = await api.post('/questionnaire-types/', {
      ...data,
      tenant_id: tenantId,
    });
    return response.data;
  },

  async updateQuestionnaireType(
    id: number,
    tenantId: number,
    data: Partial<{ name: string; title: string; path: string }>
  ): Promise<QuestionnaireType> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
    });
    const response = await api.put(`/questionnaire-types/${id}?${params}`, data);
    return response.data;
  },

  async deleteQuestionnaireType(id: number, tenantId: number): Promise<void> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
    });
    await api.delete(`/questionnaire-types/${id}?${params}`);
  },

  async getSectionValidators(): Promise<string[]> {
    const response = await api.get('/questionnaire-types/section-validators');
    return response.data;
  },

  async duplicateQuestionnaireType(id: number, tenantId: number): Promise<QuestionnaireType> {
    const params = new URLSearchParams({ tenant_id: tenantId.toString() });
    const original = (await api.get(`/questionnaire-types/${id}?${params}`)).data as QuestionnaireType;
    const response = await api.post('/questionnaire-types/', {
      name: `${original.name} (Copy)`,
      title: `${original.title} (Copy)`,
      tenant_id: tenantId,
      ...(original.type && { type: original.type }),
      ...(original.language && { language: original.language }),
      ...(original.definition && { definition: original.definition }),
    });
    return response.data;
  },

  // Patient Questionnaires
  async getPatientQuestionnaires(
    tenantId: number,
    patientId?: number,
    status?: string,
    questionnaireTypeId?: number,
    skip?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: string,
    search?: string,
  ): Promise<{ items: PatientQuestionnaire[]; total: number }> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
    });

    if (patientId) {
      params.append('patient_id', patientId.toString());
    }

    if (status) {
      params.append('status', status);
    }

    if (questionnaireTypeId) {
      params.append('questionnaire_type_id', questionnaireTypeId.toString());
    }

    if (skip !== undefined) {
      params.append('skip', skip.toString());
    }

    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }

    if (sortBy) {
      params.append('sort_by', sortBy);
    }

    if (sortOrder) {
      params.append('sort_order', sortOrder);
    }

    if (search) {
      params.append('search', search);
    }

    const response = await api.get(`/patient-questionnaires/?${params}`);
    return response.data;
  },

  async getPatientQuestionnaire(
    id: number,
    tenantId: number
  ): Promise<PatientQuestionnaireWithAnswers> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
    });
    const response = await api.get(`/patient-questionnaires/${id}?${params}`);
    return response.data;
  },

  /** Red flags raised by this submission's answers (empty unless the questionnaire
   *  type uses the medication_monitoring section validator). */
  async getQuestionnaireMonitoringFlags(
    id: number,
    tenantId: number
  ): Promise<MedicationMonitoringFlag[]> {
    const params = new URLSearchParams({ tenant_id: tenantId.toString() });
    const response = await api.get(`/patient-questionnaires/${id}/monitoring-flags?${params}`);
    return response.data.flags || [];
  },

  async createPatientQuestionnaire(
    patientId: number,
    questionnaireTypeId: number,
    tenantId: number
  ): Promise<PatientQuestionnaire> {
    const response = await api.post('/patient-questionnaires/', {
      patient_id: patientId,
      questionnaire_type_id: questionnaireTypeId,
      tenant_id: tenantId,
    });
    return response.data;
  },

  async updateQuestionnaireStatus(
    id: number,
    tenantId: number,
    newStatus: string
  ): Promise<PatientQuestionnaire> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
      new_status: newStatus,
    });
    const response = await api.post(
      `/patient-questionnaires/${id}/status?${params}`
    );
    return response.data;
  },

  async saveSectionAnswers(
    questionnaireId: number,
    tenantId: number,
    answers: SectionAnswerData[],
    lastSection?: { title: string; index: number }
  ): Promise<{ saved_count: number; answers: any[] }> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
    });

    const bulkData: BulkAnswersRequest = {
      answers: answers.map((a) => ({
        ...a,
        // Ensure answer is always an array of strings
        answer: Array.isArray(a.answer) ? a.answer : [String(a.answer)],
      })),
      ...(lastSection ? { last_section: lastSection.title, last_section_index: lastSection.index } : {}),
    };

    const response = await api.post(
      `/patient-questionnaires/${questionnaireId}/answers/bulk?${params}`,
      bulkData
    );
    return response.data;
  },

  async deletePatientQuestionnaire(id: number, tenantId: number): Promise<void> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
    });
    await api.delete(`/patient-questionnaires/${id}?${params}`);
  },

  async sendQuestionnaire(
    id: number,
    tenantId: number
  ): Promise<PatientQuestionnaire> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
    });
    const response = await api.post(
      `/patient-questionnaires/${id}/send?${params}`
    );
    return response.data;
  },

  async sendEmail(
    id: number,
    tenantId: number,
    templateId: number,
    /** Answer field holding the address, or null to use the linked patient's. */
    emailFieldName: string | null
  ): Promise<{ message: string }> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
    });
    const response = await api.post(
      `/patient-questionnaires/${id}/send-email?${params}`,
      {
        email_template_id: templateId,
        email_field_name: emailFieldName,
      }
    );
    return response.data;
  },

  async sendSms(
    id: number,
    tenantId: number,
    templateId: number,
    /** Answer field holding the number, or null to use the linked patient's. */
    phoneFieldName: string | null
  ): Promise<{ message: string }> {
    const params = new URLSearchParams({ tenant_id: tenantId.toString() });
    const response = await api.post(
      `/patient-questionnaires/${id}/send-sms?${params}`,
      { sms_template_id: templateId, phone_field_name: phoneFieldName }
    );
    return response.data;
  },

  async resendIlWebhook(
    id: number,
    tenantId: number
  ): Promise<{ message: string; questionnaire_id: number }> {
    const params = new URLSearchParams({ tenant_id: tenantId.toString() });
    const response = await api.post(
      `/patient-questionnaires/${id}/resend-il-webhook?${params}`
    );
    return response.data;
  },

  // Public API methods (no authentication required)
  // Resolve ?referral=<hash> for branding BEFORE a questionnaire row exists.
  // Once it does, the referral comes from the questionnaire itself.
  async getPublicReferral(referralHash: string): Promise<{ id: number; name: string; logo_url: string | null }> {
    const response = await api.get(`/public/referral/${referralHash}`);
    return response.data;
  },

  async getPublicQuestionnaire(token: string): Promise<PatientQuestionnaireWithAnswers> {
    const response = await api.get(`/public/questionnaires/${token}`);
    return response.data;
  },

  async savePublicAnswers(
    token: string,
    answers: SectionAnswerData[],
    lastSection?: { title: string; index: number }
  ): Promise<{ saved_count: number; answers: any[] }> {
    const bulkData: BulkAnswersRequest = {
      answers: answers.map((a) => ({
        ...a,
        answer: Array.isArray(a.answer) ? a.answer : [String(a.answer)],
      })),
      ...(lastSection ? { last_section: lastSection.title, last_section_index: lastSection.index } : {}),
    };

    const response = await api.post(
      `/public/questionnaires/${token}/answers/bulk`,
      bulkData
    );
    return response.data;
  },

  async submitPublicQuestionnaire(token: string): Promise<{ message: string; questionnaire_id: number; redirect_url?: string }> {
    const response = await api.post(`/public/questionnaires/${token}/submit`);
    return response.data;
  },

  async finishPublicQuestionnaire(token: string): Promise<{ message: string; questionnaire_id: number; redirect_url?: string }> {
    const response = await api.post(`/public/questionnaires/${token}/finish`);
    return response.data;
  },

  async exitPublicQuestionnaire(token: string, exitLocation?: string): Promise<{ message: string }> {
    const response = await api.post(`/public/questionnaires/${token}/exit`, { exit_location: exitLocation ?? null });
    return response.data;
  },

  async sendEmailVerificationCode(token: string, fieldName: string, email: string): Promise<{ sent: boolean }> {
    const response = await api.post(`/public/questionnaires/${token}/email-verification/send`, {
      field_name: fieldName,
      email,
    });
    return response.data;
  },

  async verifyEmailCode(token: string, fieldName: string, code: string): Promise<{ verified: boolean }> {
    const response = await api.post(`/public/questionnaires/${token}/email-verification/verify`, {
      field_name: fieldName,
      code,
    });
    return response.data;
  },

  leavePublicQuestionnaire(token: string): void {
    const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
    navigator.sendBeacon(`${baseUrl}/public/questionnaires/${token}/leave`);
  },

  // Get public questionnaire type by tenant and path (no auth required)
  async getPublicQuestionnaireTypeByPath(
    tenantId: number,
    path: string
  ): Promise<QuestionnaireType> {
    const response = await api.get(`/public/questionnaires/types/${tenantId}/${path}`);
    return response.data;
  },

  // Create a new public questionnaire (returns token)
  async createPublicQuestionnaire(
    tenantId: number,
    questionnaireTypeId: number,
    queryParams?: Record<string, string>,
    referralHash?: string | null
  ): Promise<{ token: string }> {
    const response = await api.post('/public/questionnaires/create', {
      tenant_id: tenantId,
      questionnaire_type_id: questionnaireTypeId,
      ...(queryParams && Object.keys(queryParams).length > 0 ? { query_params: queryParams } : {}),
      ...(referralHash ? { referral_hash: referralHash } : {}),
    });
    return response.data;
  },


  async sendQuestionnaireToPatient(
    patientId: number,
    questionnaireTypeId: number,
    emailTemplateId: number,
    tenantId: number
  ): Promise<{ success: boolean; questionnaire_id: number; token: string }> {
    const response = await api.post('/patient-questionnaires/send-to-patient', {
      patient_id: patientId,
      questionnaire_type_id: questionnaireTypeId,
      email_template_id: emailTemplateId,
      tenant_id: tenantId,
    });
    return response.data;
  },

  // Calculate score for a questionnaire (works for both public and internal questionnaires with token)
  async calculateScore(
    token: string,
    scoreName: string
  ): Promise<{
    score_name: string;
    score: string;
    score_groups: Record<string, number>;
  }> {
    const params = new URLSearchParams({
      score_name: scoreName,
    });
    const response = await api.post(
      `/public/questionnaires/${token}/score?${params}`
    );
    return response.data;
  },
};
